/**
 * @support/client 的 HTTP 实现。
 *
 * 约束来源：CONTRACT.md 第 17 行（`/v1/support` 前缀、信封结构、错误码表）、
 * 第 19/22 行（源 token 只出现在 exchange 请求体；支持 token 只走 Authorization 头，
 * ticket 只走一次性查询参数且必须脱敏）、第 32 行（附件必须走鉴权请求，不能有静态公共 URL）。
 *
 * 刻意不做的事：
 * - 不把令牌写进 URL / 查询串 / localStorage（token 由调用方以内存容器提供）；
 * - 不缓存响应（长连接与补拉由应用层决定）；
 * - 不回显原始响应正文（错误信息只取服务端已脱敏的 msg/err）。
 */

import { consumeAnswerStream, type AnswerSnapshot } from './answer-stream';
import { SupportError, transportError } from './errors';
import { parseEnvelope } from './envelope';
import type {
  AuthExchangeResult,
  Conversation,
  ConversationListQuery,
  ConversationPage,
  MessageVisibility,
  RealtimeTicket,
  SupportAttachment,
  SupportPrincipal,
} from './types';

/** exchange 的来源身份类型。 */
export type SupportSourceKind = 'customer' | 'staff';

export type HttpMethod = 'DELETE' | 'GET' | 'POST' | 'PUT';

export type QueryValue = boolean | null | number | string | undefined;

export interface SupportApiOptions {
  fetchImpl?: typeof fetch;
  /** 内存令牌读取器；返回 null 表示匿名请求（exchange 不得带令牌）。 */
  getToken?: () => null | string;
  /** 已含 `/v1/support` 前缀的 HTTP 基地址。 */
  httpBase: string;
  /** 单次请求超时毫秒，默认 15000。 */
  timeoutMs?: number;
}

export interface ApiRequestOptions {
  /** 显式匿名请求（不带 Authorization），仅身份引导接口使用。 */
  anonymous?: boolean;
  body?: unknown;
  method?: HttpMethod;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

export interface SupportApi {
  streamAnswer?(conversationId: string, signal: AbortSignal, onAnswer: (snapshot: AnswerSnapshot) => void): Promise<void>;
  createConversation(clientRequestId: string): Promise<Conversation>;
  downloadAttachment(attachmentId: string): Promise<Blob>;
  exchange(source: SupportSourceKind, sourceToken: string): Promise<AuthExchangeResult>;
  fetchTicket(): Promise<RealtimeTicket>;
  getConversation(conversationId: string): Promise<Conversation>;
  readonly httpBase: string;
  listConversations(query?: ConversationListQuery): Promise<ConversationPage>;
  logout(): Promise<void>;
  me(): Promise<SupportPrincipal>;
  request<T>(path: string, options?: ApiRequestOptions): Promise<T>;
  uploadAttachment(
    conversationId: string,
    file: Blob,
    visibility: MessageVisibility,
    fileName?: string,
  ): Promise<SupportAttachment>;
}

const DEFAULT_TIMEOUT_MS = 15_000;

/** 拼接基地址、路径与查询串；undefined / null / 空串一律省略。 */
export function buildApiUrl(
  httpBase: string,
  path: string,
  query?: Record<string, QueryValue>,
): string {
  const base = httpBase.replace(/\/+$/u, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const url = `${base}${suffix}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search === '' ? url : `${url}?${search}`;
}

/**
 * HTTP 状态码 -> 结构化错误类别。
 * 仅用于响应体不是协议信封（网关错误页 / 代理拒绝）时的兜底归类，
 * 协议信封存在时一律以信封 code 为准。
 */
export function kindForHttpStatus(status: number): SupportError['kind'] {
  switch (status) {
    case 400: {
      return 'invalid';
    }
    case 401: {
      return 'unauthorized';
    }
    case 403: {
      return 'forbidden';
    }
    case 404: {
      return 'not-found';
    }
    case 409: {
      return 'conflict';
    }
    case 413: {
      return 'too-large';
    }
    case 429: {
      return 'capacity';
    }
    default: {
      return status >= 500 ? 'internal' : 'protocol';
    }
  }
}

interface RawResult {
  response: Response;
  status: number;
}

export function createSupportApi(options: SupportApiOptions): SupportApi {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const getToken = options.getToken ?? (() => null);
  const httpBase = options.httpBase.replace(/\/+$/u, '');
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const rawRequest = async (
    path: string,
    request: ApiRequestOptions & { anonymous?: boolean; form?: FormData },
  ): Promise<RawResult> => {
    if (typeof fetchImpl !== 'function') {
      throw transportError('network', '当前环境没有可用的 fetch 实现');
    }
    const url = buildApiUrl(httpBase, path, request.query);
    const headers: Record<string, string> = { accept: 'application/json' };
    // exchange 是身份引导接口：绝不能带旧的客服令牌，
    // 否则服务端可能把上一次身份与本次源身份混在一起判定。
    const token = request.anonymous === true ? null : getToken();
    if (token) headers.authorization = `Bearer ${token}`;

    let body: BodyInit | undefined;
    if (request.form) {
      // 不手写 content-type：必须让浏览器带 multipart boundary。
      body = request.form;
    } else if (request.body !== undefined) {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(request.body);
    }

    const controller =
      typeof AbortController === 'function' ? new AbortController() : null;
    let timedOut = false;
    const timer =
      controller === null
        ? null
        : setTimeout(() => {
            timedOut = true;
            controller.abort();
          }, timeoutMs);
    const onExternalAbort = (): void => controller?.abort();
    request.signal?.addEventListener('abort', onExternalAbort);

    let response: Response;
    try {
      response = await fetchImpl(url, {
        ...(body === undefined ? {} : { body }),
        headers,
        method: request.method ?? 'GET',
        ...(controller ? { signal: controller.signal } : {}),
      });
    } catch (error) {
      if (timedOut) {
        throw transportError('timeout', `请求超时（${timeoutMs}ms）`);
      }
      throw transportError(
        'network',
        error instanceof Error ? error.message : String(error),
      );
    } finally {
      if (timer !== null) clearTimeout(timer);
      request.signal?.removeEventListener('abort', onExternalAbort);
    }
    return { response, status: response.status };
  };

  const parseBody = async <T>(result: RawResult): Promise<T> => {
    const text = await result.response.text();
    if (text.trim() === '') {
      // 204 / 空体：协议要求信封，但空体只可能出现在无返回值接口上，
      // 这里返回 undefined 而不是伪造数据。
      return undefined as T;
    }
    let payload: unknown;
    try {
      payload = JSON.parse(text);
    } catch {
      throw new SupportError({
        code: 0,
        kind: kindForHttpStatus(result.status),
        message: `响应不是合法 JSON（HTTP ${result.status}）`,
      });
    }
    return parseEnvelope<T>(payload);
  };

  const api: SupportApi = {
    httpBase,
    async streamAnswer(conversationId, signal, onAnswer) {
      const token = getToken();
      const response = await fetchImpl(buildApiUrl(httpBase, `/conversations/${encodeURIComponent(conversationId)}/answer-stream`), {
        headers: { accept: 'text/event-stream', ...(token ? { authorization: `Bearer ${token}` } : {}) },
        cache: 'no-store',
        signal,
      });
      if (!response.ok || !response.body || !response.headers.get('content-type')?.includes('text/event-stream')) {
        void response.body?.cancel().catch(() => {});
        throw new SupportError({ code: 0, kind: kindForHttpStatus(response.status), message: `回复连接失败（HTTP ${response.status}）` });
      }
      await consumeAnswerStream(response.body, signal, onAnswer);
    },
    async createConversation(clientRequestId: string) {
      return await api.request<Conversation>('/conversations', {
        body: { clientRequestId },
        method: 'POST',
      });
    },
    async downloadAttachment(attachmentId: string) {
      const result = await rawRequest(
        `/attachments/${encodeURIComponent(attachmentId)}/download`,
        {},
      );
      if (!result.response.ok) {
        await parseBody<unknown>(result); // 非 2xx 时以信封错误暴露
        throw new SupportError({
          code: 0,
          kind: kindForHttpStatus(result.status),
          message: `附件下载失败（HTTP ${result.status}）`,
        });
      }
      return await result.response.blob();
    },
    async exchange(source: SupportSourceKind, sourceToken: string) {
      return await api.request<AuthExchangeResult>('/auth/exchange', {
        anonymous: true,
        body: { source, token: sourceToken },
        method: 'POST',
      });
    },
    async fetchTicket() {
      return await api.request<RealtimeTicket>('/realtime/ticket', { method: 'POST' });
    },
    async getConversation(conversationId: string) {
      return await api.request<Conversation>(
        `/conversations/${encodeURIComponent(conversationId)}`,
      );
    },
    async listConversations(query?: ConversationListQuery) {
      return await api.request<ConversationPage>('/conversations', {
        query: {
          cursor: query?.cursor,
          limit: query?.limit,
          query: query?.query,
          status: query?.status,
        },
      });
    },
    async logout() {
      await api.request<unknown>('/auth/logout', { method: 'POST' });
    },
    async me() {
      return await api.request<SupportPrincipal>('/me');
    },
    async request<T>(path: string, requestOptions?: ApiRequestOptions) {
      const result = await rawRequest(path, requestOptions ?? {});
      return await parseBody<T>(result);
    },
    async uploadAttachment(
      conversationId: string,
      file: Blob,
      visibility: MessageVisibility,
      fileName?: string,
    ) {
      const form = new FormData();
      form.append('file', file, fileName ?? 'upload');
      form.append('visibility', visibility);
      const result = await rawRequest(
        `/conversations/${encodeURIComponent(conversationId)}/attachments`,
        { form, method: 'POST' },
      );
      return await parseBody<SupportAttachment>(result);
    },
  };

  return api;
}